// T424 — the single owner of every host this app knows about.
//
// THIS IS THE ONLY MODULE ALLOWED TO DECLARE A HOST LITERAL.
// `scripts/maintenance/lint-host-ssot.mjs` hard-fails on any other one (the sole
// exception is `auth.server.ts`'s trusted-origins list, which is legitimately a
// list and pre-authorises the future domain).
//
// ── Why three constants and not one ────────────────────────────────────────────
//
// "Put the domain in one place" is the right instinct with a wrong literal
// reading: these are THREE different questions that happen to have had the same
// answer once, and collapsing them re-introduces the bug this ticket removed.
//
//   SERVING_ORIGIN      "where does the app answer?"     → moves at migration
//   BRAND_ENTITY_ORIGIN "which company is this?"          → NEVER moves
//   APP_LINK_BASE       "where do I send a human?"        → moves at migration
//
// Tying identity to the serving host would change the brand's identity the day
// the domain migrates, and every external reference (galacalero.com's `worksFor`
// edge) would point at a node that no longer exists — engines would read the
// post-migration company as a brand new entity. An `@id` is an identifier, not a
// URL that has to resolve; a link base is the exact opposite.

/**
 * Where the app actually answers today. Feeds `SITE_ORIGIN` and through it every
 * canonical, hreflang, sitemap `<loc>` and Open Graph URL.
 *
 * The apex since T584 (2026-09-24), the second and last hop. The order that made it
 * safe is worth keeping: DNS first, verify the apex serves, deploy second — until
 * that morning the apex answered `Connection reset by peer` and `www` served the
 * outgoing Zenler site, and a canonical at a host that cannot answer points every
 * crawler and every emailed CTA at nothing.
 *
 * `app.thefittclub.com` (T511's host) stays alive as the emergency alias and keeps
 * serving the members who installed the app from it; it is no longer own-origin.
 *
 * It left `thefittclub.pages.dev` in T511 for a reason worth keeping written down:
 * `pages.dev` is not merely off-brand, it is unreachable for a large share of this
 * product's audience. T651 measured it handing real Spanish clients
 * `188.114.96.5 / 97.5`, blocked across all five national ISPs, while the zone's own
 * hosts resolved to `104.21.46.57 / 172.67.168.28` and were never blocked. A
 * canonical, a sitemap `<loc>` and a password-reset link pointing at a blocked range
 * are the same defect wearing three hats.
 *
 * Note this is a FIXED origin, never the request host — a preview deployment must
 * never claim to be the canonical copy under its own ephemeral hostname.
 */
export const SERVING_ORIGIN = "https://thefittclub.com";

/**
 * The permanent identity of the brand, used only for JSON-LD `@id`s.
 *
 * Points at `thefittclub.com`. Since T584 that host also serves, so this and
 * `SERVING_ORIGIN` hold the same string — a coincidence of values, not an alias: an
 * `@id` identifies an entity and does not have to dereference. Its
 * only job is to be stable and globally unique, so it is chosen once and never
 * changed — including on migration day, when the other two constants DO change.
 */
export const BRAND_ENTITY_ORIGIN = "https://thefittclub.com";

/**
 * Base for links a human will click: email CTAs, the one-click unsubscribe link,
 * cron-generated deep links.
 *
 * MUST be a host that answers. This is the opposite requirement to
 * `BRAND_ENTITY_ORIGIN` — nobody dereferences an `@id`, but everybody
 * dereferences an email CTA.
 *
 * Before T424 iteration 2 the services hardcoded the parked apex here, and the
 * background worker does not bind `BETTER_AUTH_URL`, so that was the *normal*
 * value for the daily crons rather than an edge case: every lead-drip CTA — the
 * auto-test sequence's "Ver mi resultado" among them — resolved to a connection
 * reset.
 *
 * Declared as its own literal rather than aliased to `SERVING_ORIGIN`, for two
 * reasons found by an independent audit of this change:
 *   1. Writing `export const APP_LINK_BASE = SERVING_ORIGIN` trips the dead-export
 *      gate's `duplicates` rule, which flags two exports that resolve to the SAME
 *      SYMBOL. Note this is narrower than it first sounds, and an AI review was
 *      right to push on the original wording here: two constants holding an equal
 *      *string* are fine (these two do today), and a re-export alias elsewhere is
 *      fine too (`site.ts` does `export const SITE_ORIGIN = SERVING_ORIGIN`) —
 *      because there the alias is the module's whole purpose. Only the
 *      alias-in-place form failed, and it did fail CI, which is why it is recorded.
 *   2. More importantly, aliasing quietly removes the ability to STAGE the
 *      migration. The realistic path is not "both move at once": the apex becomes
 *      canonical the moment DNS is live, while member-facing links should not move
 *      until the apex is verified to actually serve member traffic. An alias makes
 *      that impossible to express, and it also turns the link-base test into a
 *      tautology the day someone un-aliases it.
 *
 * T511 moved both to `app.thefittclub.com` in one step, and reason 2 is why that
 * was allowed rather than sloppy: `app.` had already been serving production for
 * weeks (T509), so there was no unverified host left to stage against. The ability
 * to stage is what matters, not exercising it on every hop. T584 did not need it
 * either: the apex was verified serving before the deploy that moved both.
 */
export const APP_LINK_BASE = "https://thefittclub.com";

/**
 * Hostname (no scheme) of the serving origin, for code that compares against
 * `window.location.hostname` rather than building a URL — `sentry.client.ts`'s
 * environment detection is the current consumer.
 *
 * Derived, not a second literal: a hardcoded copy is exactly the drift this
 * module exists to prevent, and the audit that added this found one already.
 */
export const SERVING_HOST = new URL(SERVING_ORIGIN).hostname;

/**
 * Suffix of the per-PR Cloudflare Pages preview subdomains. Distinct from
 * `SERVING_HOST`: preview deployments stay on `pages.dev` after the brand domain
 * migrates, so this does NOT move with the other constants.
 */
export const PREVIEW_HOST_SUFFIX = ".thefittclub.pages.dev";

/**
 * T574 — the hosts that answer but are not the canonical copy, and how each one is
 * retired. Both keep serving whatever they are not redirected for; the decision is
 * `decideHostRedirect` in `~/lib/domain/host-redirect`, applied first in `functions/[[path]].ts`.
 *
 *   RETIRED_HOST — the bare Pages host T511 left behind. Blocked across all five Spanish
 *                  ISPs (T651) and a second production front door nobody canonicalises:
 *                  every page request on it goes to `SERVING_ORIGIN`. Derived from
 *                  `PREVIEW_HOST_SUFFIX` so the name exists once; the per-PR previews
 *                  that share its suffix are a different host and never match.
 *   ALIAS_HOST   — T511's host, kept alive by T584 as the emergency alias and for the
 *                  members who installed the app from it. Only the indexable public
 *                  pages move; the panel, the player and the auth screens stay, so an
 *                  installed app is never bounced out of its own origin (T512).
 */
export const RETIRED_HOST = PREVIEW_HOST_SUFFIX.slice(1);
export const ALIAS_HOST = "app.thefittclub.com";

/**
 * The DNS zone we control — the registrable domain, not an origin (T730).
 *
 * A fourth thing, and the reason it is not any of the three above: those answer
 * "where does X point?", this one answers "is this host ours to defend?". It is the
 * predicate `isServingHostInOurZone` runs against every `MEDIA_PUBLIC_URL`, and the
 * ticket it comes from is about a host that was NOT ours — on 2026-09-12 an R2
 * development subdomain resolved into a Spanish block list while this zone stayed
 * clean, and the app served video and not one image.
 *
 * Derived from `BRAND_ENTITY_ORIGIN` rather than written out, because the whole point
 * of this module is that the name appears once. `BRAND_ENTITY_ORIGIN` is the right
 * parent: it is the constant documented as never changing, which is exactly the
 * property a zone needs — `SERVING_ORIGIN` has moved twice (T511, T584) and could again.
 */
export const OUR_ZONE = new URL(BRAND_ENTITY_ORIGIN).hostname;

/**
 * If this is one of our own https origins, return its NORMALIZED origin; otherwise
 * `null`.
 *
 * Parses the URL rather than testing the raw string, so
 * `https://thefittclub.pages.dev.evil.com` fails — a plain `endsWith` on the
 * suffix would wave it through.
 *
 * The scheme is checked HERE and not only at the call site, so the helper is
 * correct in isolation: `http://app.thefittclub.com` passes a hostname-only test
 * but must never become a link base. This resolver is shared by four services and
 * will be inherited by callers that do not exist yet; a predicate that is only safe
 * given its current caller is the kind of thing that stops being true quietly.
 *
 * It returns `url.origin` rather than a boolean for the same reason. Validating a
 * parsed URL and then emitting the caller's raw string lets everything the parse
 * ignored ride along:
 *   * `https://evil.com@thefittclub.pages.dev` — userinfo. Resolves to our host, so
 *     it is not an exfiltration path, but the member SEES `evil.com` in the link.
 *   * `https://thefittclub.pages.dev/r?to=` — a path and query, which concatenate
 *     into `…/r?to=/es/auto-test` and produce a link that simply does not work.
 * `url.origin` drops userinfo, path, query, fragment and a default port, so the
 * value that leaves here is the one that was actually validated.
 *
 * Both brand hosts are covered: production matches `SERVING_HOST` exactly, and
 * per-PR previews match the leading-dot `PREVIEW_HOST_SUFFIX`.
 */
function ownOrigin(origin: string): string | null {
  let url: URL;
  try {
    url = new URL(origin);
  } catch {
    return null;
  }
  if (url.protocol !== "https:") return null;
  if (url.hostname !== SERVING_HOST && !url.hostname.endsWith(PREVIEW_HOST_SUFFIX)) return null;
  return url.origin;
}

/**
 * The one resolver for a member-facing link base. Before T424 iteration 2 this
 * rule existed four times: identically as `appUrl(env)` in `retention.server.ts`
 * and `email-sequences.server.ts`, inlined in `unsubscribe.server.ts`, and as a
 * superset in `leads.server.ts`'s `resolveEmailBase`.
 *
 * Precedence, widest-context first:
 *   1. `requestOrigin` — so an email sent from a preview links back to that same
 *      preview instead of to production. Only `leads.server.ts` has a request in
 *      hand; the crons do not, which is why this is a parameter and not a fifth copy.
 *      **Accepted only for our own hosts, and normalized** — see the trust note below.
 *   2. `BETTER_AUTH_URL` — the deployment's own base when it is bound.
 *   3. `APP_LINK_BASE` — a host that answers. Never the parked apex.
 *
 * ## Why `requestOrigin` is allowlisted rather than trusted
 *
 * The behaviour inherited from `resolveEmailBase` (T71) accepted ANY `https://`
 * origin the caller passed. That was safe by accident, not by design: today the
 * one caller derives the origin from the served request and the resulting link
 * carries no secret (`?r=<result>`). But this is now the ONLY resolver, so every
 * future caller inherits whatever it decides — and an emailed link built from a
 * request-derived origin is host-header shaped. The day one of those links carries
 * a token, an attacker-influenced `Host` becomes an exfiltration path.
 *
 * Restricting to `SERVING_HOST` + `PREVIEW_HOST_SUFFIX` keeps the preview-links-to-
 * preview behaviour that motivated the parameter, and costs nothing else: an origin
 * that fails the check falls through to `BETTER_AUTH_URL`, which is the deployment's
 * own base.
 *
 * Trailing slashes are stripped so callers can concatenate paths freely.
 */
export function resolveLinkBase(
  env: { BETTER_AUTH_URL?: string },
  requestOrigin?: string,
): string {
  const own = requestOrigin ? ownOrigin(requestOrigin) : null;
  if (own) return own;
  const bound = env.BETTER_AUTH_URL;
  if (bound?.startsWith("https://")) return bound.replace(/\/+$/, "");
  return APP_LINK_BASE;
}
